import { useEffect, useMemo, useState } from 'react';
import { createFileRoute, Link } from '@tanstack/react-router';
import { trpc, type RouterOutputs } from '@org/clients/school';
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Input,
  renderInlineMarkdown,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Skeleton,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@org/ui';
import { useTranslation } from 'react-i18next';
import { useTeacherId } from '../../components/teacher-picker';
import {
  CategoryPills,
  FlagPill,
  TeacherFlagSummary,
  metricsWeeks,
  useAutoFlagReasonLines,
  type FlagLevel,
  type TeacherFlagLevel,
} from '../../components/student-flag-badges';
import { StudentFlagDialog } from '../../components/student-flag-dialog';
import { ExtraClassDots, SemesterDots, SessionDot, type DotSession } from '../../components/semester-dots';

export const Route = createFileRoute('/students/')({
  component: StudentsPage,
  validateSearch: (search: Record<string, unknown>) => ({
    teacherId: typeof search.teacherId === 'string' ? search.teacherId : undefined,
  }),
});

const FLAG_FILTER_OPTIONS: FlagLevel[] = ['red', 'yellow', 'green', 'none'];

type StudentItem = RouterOutputs['teacher']['students']['list']['students'][number];
type GroupOverview = RouterOutputs['teacher']['students']['groupOverview'];
type GroupStudent = GroupOverview['students'][number];
type GroupSession = GroupOverview['sessions'][number];

const selectTriggerClass =
  'rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm shadow-sm focus:border-[#cf2e2e] focus:outline-none';

function StudentsPage() {
  const { t } = useTranslation('admin');
  const teacherId = useTeacherId();
  const { data, isLoading } = trpc.teacher.students.list.useQuery({ teacherId });
  const students = data?.students ?? [];

  const [tab, setTab] = useState<'cards' | 'byGroup'>('cards');
  const [selectedGroup, setSelectedGroup] = useState<string>('all');
  const [selectedFlag, setSelectedFlag] = useState<string>('all');
  const [studentSearch, setStudentSearch] = useState<string>('');
  const [flagTarget, setFlagTarget] = useState<{
    studentId: string;
    displayName: string;
    flag: TeacherFlagLevel | null;
  } | null>(null);

  const groupOptions = useMemo(() => {
    const allGroups = new Set<string>();
    students.forEach((student) => student.groups.forEach((g) => allGroups.add(g)));
    return Array.from(allGroups).sort();
  }, [students]);

  const visibleStudents = useMemo(() => {
    const search = studentSearch.toLowerCase().trim();
    return students.filter((s) => {
      if (selectedGroup !== 'all' && !s.groups.includes(selectedGroup)) return false;
      if (selectedFlag !== 'all' && s.autoFlag.level !== selectedFlag && s.teacherFlag?.flag !== selectedFlag) {
        return false;
      }
      if (search && !s.displayName.toLowerCase().includes(search)) return false;
      return true;
    });
  }, [students, selectedGroup, selectedFlag, studentSearch]);

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <p className="text-sm uppercase tracking-[0.3em] text-[#cf2e2e]">{t('teacher.students.eyebrow')}</p>
          <h1 className="text-3xl font-semibold text-gray-900">{t('teacher.students.title')}</h1>
          <p className="text-sm text-gray-600">{t('teacher.students.description')}</p>
        </div>
        <Badge variant="outline" className="rounded-full border-rose-200 bg-white px-4 py-1 text-[#cf2e2e]">
          {t('teacher.students.countBadge', { count: visibleStudents.length })}
        </Badge>
      </div>

      <Tabs value={tab} onValueChange={(value) => setTab(value === 'byGroup' ? 'byGroup' : 'cards')}>
        <TabsList className="bg-[#fff5f8]">
          <TabsTrigger value="cards" className="data-[state=active]:text-[#cf2e2e]">
            {t('teacher.students.tabs.cards')}
          </TabsTrigger>
          <TabsTrigger value="byGroup" className="data-[state=active]:text-[#cf2e2e]">
            {t('teacher.students.tabs.byGroup')}
          </TabsTrigger>
        </TabsList>

        <TabsContent value="cards" className="mt-4 flex flex-col gap-6">
          {students.length > 0 && (
            <div className="flex flex-wrap gap-3 rounded-xl border border-rose-50 bg-white p-4 shadow-sm">
              {groupOptions.length > 0 && (
                <div className="flex flex-col gap-1">
                  <label className="text-xs uppercase tracking-[0.2em] text-gray-500">
                    {t('teacher.filters.group')}
                  </label>
                  <Select value={selectedGroup} onValueChange={setSelectedGroup}>
                    <SelectTrigger className={`min-w-[200px] ${selectTriggerClass}`}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">{t('teacher.filters.groupAll')}</SelectItem>
                      {groupOptions.map((group) => (
                        <SelectItem key={group} value={group}>
                          {group}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
              <div className="flex flex-col gap-1">
                <label className="text-xs uppercase tracking-[0.2em] text-gray-500">
                  {t('teacher.students.filterFlag')}
                </label>
                <Select value={selectedFlag} onValueChange={setSelectedFlag}>
                  <SelectTrigger className={`min-w-[180px] ${selectTriggerClass}`}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">{t('teacher.students.filterFlagAll')}</SelectItem>
                    {FLAG_FILTER_OPTIONS.map((level) => (
                      <SelectItem key={level} value={level}>
                        {t(`teacher.flags.levels.${level}`)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex flex-col gap-1">
                <label className="text-xs uppercase tracking-[0.2em] text-gray-500">
                  {t('teacher.filters.student')}
                </label>
                <Input
                  type="text"
                  className={`min-w-[220px] ${selectTriggerClass}`}
                  placeholder={t('teacher.filters.searchStudent')}
                  value={studentSearch}
                  onChange={(e) => setStudentSearch(e.target.value)}
                />
              </div>
            </div>
          )}

          {isLoading && (
            <div className="grid gap-4 md:grid-cols-2">
              {Array.from({ length: 4 }).map((_, index) => (
                // eslint-disable-next-line react/no-array-index-key
                <Card key={index} className="border border-rose-50 bg-white">
                  <CardHeader>
                    <Skeleton className="h-6 w-1/2" />
                    <Skeleton className="h-4 w-1/3" />
                  </CardHeader>
                  <CardContent className="space-y-2">
                    <Skeleton className="h-4 w-full" />
                    <Skeleton className="h-4 w-5/6" />
                    <Skeleton className="h-4 w-2/3" />
                  </CardContent>
                </Card>
              ))}
            </div>
          )}

          {!isLoading && students.length === 0 && (
            <Card className="border border-dashed border-rose-200 bg-white/80">
              <CardHeader>
                <CardTitle>{t('teacher.students.emptyTitle')}</CardTitle>
                <CardDescription>{t('teacher.students.emptyDescription')}</CardDescription>
              </CardHeader>
            </Card>
          )}

          {!isLoading && students.length > 0 && visibleStudents.length === 0 && (
            <Card className="border border-dashed border-rose-200 bg-white/80">
              <CardHeader>
                <CardTitle>{t('teacher.students.noMatchTitle')}</CardTitle>
                <CardDescription>{t('teacher.students.noMatchDescription')}</CardDescription>
              </CardHeader>
            </Card>
          )}

          <div className="grid gap-4 md:grid-cols-2">
            {visibleStudents.map((student) => (
              <StudentCard
                key={student.studentId}
                student={student}
                onFlag={() =>
                  setFlagTarget({
                    studentId: student.studentId,
                    displayName: student.displayName,
                    flag: student.teacherFlag?.flag ?? null,
                  })
                }
              />
            ))}
          </div>
        </TabsContent>

        <TabsContent value="byGroup" className="mt-4">
          <GroupOverviewTab
            onFlag={(student) =>
              setFlagTarget({
                studentId: student.studentId,
                displayName: student.displayName,
                flag: student.teacherFlag?.flag ?? null,
              })
            }
          />
        </TabsContent>
      </Tabs>

      {flagTarget && (
        <StudentFlagDialog
          open
          onOpenChange={(open) => {
            if (!open) setFlagTarget(null);
          }}
          studentId={flagTarget.studentId}
          studentName={flagTarget.displayName}
          currentFlag={flagTarget.flag}
        />
      )}
    </div>
  );
}

function rateText(t: (key: string, options?: Record<string, unknown>) => string, key: string, rate: number | null) {
  return rate !== null ? t(key, { rate }) : t('teacher.flags.metrics.noData');
}

function StudentCard({ student, onFlag }: { student: StudentItem; onFlag: () => void }) {
  const { t } = useTranslation('admin');
  const reasonLines = useAutoFlagReasonLines();
  const autoTitle = reasonLines(student.autoFlag).join('; ') || t('teacher.flags.noneHint');

  const semesterLine = student.semester.hasGroupDates
    ? t('teacher.students.semesterSince', { date: new Date(student.metrics.since).toLocaleDateString() })
    : t('teacher.students.noGroupDates', { weeks: metricsWeeks(student.metrics) });

  const communityStat =
    student.metrics.community.rate !== null
      ? t('teacher.students.communityRate', {
          done: student.metrics.community.done,
          total: student.metrics.community.total,
        })
      : t('teacher.flags.metrics.noData');

  return (
    <Card className="flex h-full flex-col border border-rose-50 bg-white shadow-sm">
      <CardHeader className="space-y-3">
        <div className="flex items-start justify-between gap-3">
          <CardTitle className="text-xl text-gray-900">
            <Link
              to="/students/$studentId"
              params={{ studentId: student.studentId }}
              search={{ teacherId: undefined }}
              className="hover:text-[#cf2e2e] hover:underline"
            >
              {student.displayName}
            </Link>
          </CardTitle>
          <Badge className="shrink-0 bg-[#fff5f8] text-[#cf2e2e]">
            {student.groups[0] ?? t('teacher.students.noGroup')}
          </Badge>
        </div>
        <CardDescription className="text-xs uppercase tracking-wide text-gray-400">
          {student.groups.length > 1
            ? t('teacher.students.groupsCount', { count: student.groups.length })
            : student.groups[0] ?? t('teacher.students.individual')}
        </CardDescription>
        <div className="flex flex-wrap items-center gap-2">
          <FlagPill
            level={student.autoFlag.level}
            label={`${t('teacher.flags.auto')} · ${t(`teacher.flags.levels.${student.autoFlag.level}`)}`}
            title={autoTitle}
          />
        </div>
        <CategoryPills autoFlag={student.autoFlag} />
        {student.teacherFlag && <TeacherFlagSummary flag={student.teacherFlag} clampComment />}
      </CardHeader>
      <CardContent className="flex flex-1 flex-col gap-4">
        <DotSection
          label={t('teacher.students.presence')}
          stat={rateText(t, 'teacher.students.presenceRate', student.metrics.presence.rate)}
        >
          <SemesterDots sessions={student.sessions} kind="presence" studentId={student.studentId} size="sm" />
        </DotSection>
        <DotSection
          label={t('teacher.students.homework')}
          stat={rateText(t, 'teacher.students.homeworkSemesterRate', student.metrics.homework.rate)}
        >
          <SemesterDots sessions={student.sessions} kind="homework" studentId={student.studentId} size="sm" />
        </DotSection>
        <DotSection label={t('teacher.students.extraClasses')} stat={communityStat}>
          <ExtraClassDots events={student.communityEvents} size="sm" minSlots={5} />
        </DotSection>
        <p className="text-xs text-gray-400">{semesterLine}</p>
        <div className="text-sm text-gray-600">
          {student.leveling
            ? renderInlineMarkdown(student.leveling, `student-${student.studentId}-leveling`)
            : t('teacher.students.noLeveling')}
        </div>
        <div className="mt-auto flex flex-wrap gap-2 pt-2">
          <Button variant="outline" size="sm" asChild className="text-xs">
            <Link to="/students/$studentId" params={{ studentId: student.studentId }} search={{ teacherId: undefined }}>
              {t('teacher.students.viewProfile')}
            </Link>
          </Button>
          <Button size="sm" className="text-xs" onClick={onFlag}>
            {t('teacher.students.flagButton')}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

function DotSection({ label, stat, children }: { label: string; stat: string; children: React.ReactNode }) {
  return (
    <div className="space-y-2">
      <div className="flex items-baseline justify-between gap-3">
        <p className="text-xs uppercase tracking-[0.3em] text-gray-500">{label}</p>
        <p className="text-right text-xs text-gray-500">{stat}</p>
      </div>
      {children}
    </div>
  );
}

/* ----------------------------- By group tab ----------------------------- */

const shortDate = (value: Date | string) =>
  new Date(value).toLocaleDateString(undefined, { day: '2-digit', month: '2-digit' });

function GroupOverviewTab({ onFlag }: { onFlag: (student: GroupStudent) => void }) {
  const { t } = useTranslation('admin');
  const teacherId = useTeacherId();
  const [groupId, setGroupId] = useState<string | undefined>(undefined);
  const { data, isLoading } = trpc.teacher.students.groupOverview.useQuery({ teacherId, groupId });

  // Pick the first group once we know which ones exist (or if the chosen one vanished).
  useEffect(() => {
    if (!data) return;
    if (data.groups.length === 0) return;
    if (!groupId || !data.groups.some((group) => group.groupId === groupId)) {
      setGroupId(data.groups[0].groupId);
    }
  }, [data, groupId]);

  const groups = data?.groups ?? [];
  const sessions = data?.sessions ?? [];
  const students = data?.students ?? [];
  const showTable = data && data.group && data.group.groupId === groupId;

  return (
    <Card className="border border-rose-50 bg-white shadow-sm">
      <CardHeader className="gap-3">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <CardTitle className="text-xl text-gray-900">{t('teacher.students.groupTable.title')}</CardTitle>
            <CardDescription>{t('teacher.students.groupTable.description')}</CardDescription>
          </div>
          {showTable && (
            <Badge variant="outline" className="rounded-full border-rose-200 bg-white px-3 py-1 text-[#cf2e2e]">
              {t('teacher.students.groupTable.sessionsCount', { count: sessions.length })}
            </Badge>
          )}
        </div>
        {groups.length > 0 && (
          <div className="flex flex-col gap-1">
            <label className="text-xs uppercase tracking-[0.2em] text-gray-500">
              {t('teacher.students.groupTable.selectGroup')}
            </label>
            <Select value={groupId ?? ''} onValueChange={(value) => setGroupId(value || undefined)}>
              <SelectTrigger className={`w-full max-w-sm ${selectTriggerClass}`}>
                <SelectValue placeholder={t('teacher.students.groupTable.selectPlaceholder')} />
              </SelectTrigger>
              <SelectContent>
                {groups.map((group) => (
                  <SelectItem key={group.groupId} value={group.groupId}>
                    {group.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}
      </CardHeader>
      <CardContent>
        {(isLoading || (!showTable && groups.length > 0)) && (
          <div className="space-y-2">
            <Skeleton className="h-6 w-full" />
            <Skeleton className="h-6 w-5/6" />
            <Skeleton className="h-6 w-2/3" />
          </div>
        )}
        {!isLoading && groups.length === 0 && <EmptyHint text={t('teacher.students.groupTable.noGroups')} />}
        {showTable && students.length === 0 && <EmptyHint text={t('teacher.students.groupTable.empty')} />}
        {showTable && students.length > 0 && (
          <GroupTable sessions={sessions} students={students} onFlag={onFlag} />
        )}
      </CardContent>
    </Card>
  );
}

function GroupTable({
  sessions,
  students,
  onFlag,
}: {
  sessions: GroupSession[];
  students: GroupStudent[];
  onFlag: (student: GroupStudent) => void;
}) {
  const { t } = useTranslation('admin');
  const headCell = 'px-2 py-2 text-[10px] font-medium uppercase tracking-[0.15em] text-gray-500';

  return (
    <div className="overflow-x-auto">
      <table className="min-w-full border-separate border-spacing-0 text-sm">
        <thead>
          <tr>
            <th className={`${headCell} sticky left-0 z-10 bg-white text-left`}>
              {t('teacher.students.groupTable.columns.student')}
            </th>
            <th className={headCell} />
            {sessions.map((session) => (
              <th
                key={session.eventId}
                title={session.className ?? session.title ?? t('common.fallbacks.class')}
                className={`${headCell} whitespace-nowrap text-center font-normal normal-case tracking-normal text-gray-600 ${
                  session.isPast ? '' : 'text-gray-400'
                }`}
              >
                {shortDate(session.eventDate)}
              </th>
            ))}
            <th className={`${headCell} whitespace-nowrap text-center`}>
              {t('teacher.students.groupTable.columns.presenceTotal')}
            </th>
            <th className={`${headCell} whitespace-nowrap text-center`}>
              {t('teacher.students.groupTable.columns.homeworkTotal')}
            </th>
            <th className={`${headCell} whitespace-nowrap text-center`}>
              {t('teacher.students.groupTable.columns.alert')}
            </th>
          </tr>
        </thead>
        <tbody>
          {students.map((student) => (
            <GroupRows key={student.studentId} student={student} sessions={sessions} onFlag={() => onFlag(student)} />
          ))}
        </tbody>
      </table>
    </div>
  );
}

function GroupRows({
  student,
  sessions,
  onFlag,
}: {
  student: GroupStudent;
  sessions: GroupSession[];
  onFlag: () => void;
}) {
  const { t } = useTranslation('admin');
  const reasonLines = useAutoFlagReasonLines();
  const autoTitle = reasonLines(student.autoFlag).join('; ') || t('teacher.flags.noneHint');

  const dotSessions: DotSession[] = sessions.map((session, index) => {
    const cell = student.cells[index];
    return {
      eventId: session.eventId,
      title: session.title,
      eventDate: session.eventDate,
      classId: session.classId,
      className: session.className,
      scorecardId: session.scorecardId,
      isPast: session.isPast,
      isPresent: cell?.isPresent ?? null,
      homework: cell?.homework ?? null,
    };
  });

  const rowLabel = 'px-2 py-1 text-[10px] uppercase tracking-[0.15em] text-gray-400 whitespace-nowrap';
  const dotCell = 'px-2 py-1 text-center';
  const totalCell = 'px-2 py-1 text-center text-sm text-gray-800 align-middle';
  const topBorder = 'border-t border-rose-50';

  return (
    <>
      <tr>
        <td rowSpan={2} className={`${topBorder} sticky left-0 z-10 bg-white px-2 py-2 align-middle`}>
          <Link
            to="/students/$studentId"
            params={{ studentId: student.studentId }}
            search={{ teacherId: undefined }}
            className="whitespace-nowrap font-medium text-gray-900 hover:text-[#cf2e2e] hover:underline"
          >
            {student.displayName}
          </Link>
          {student.teacherFlag && (
            <div className="mt-1">
              <FlagPill
                level={student.teacherFlag.flag}
                label={`${t('teacher.flags.manual')} · ${t(`teacher.flags.levels.${student.teacherFlag.flag}`)}`}
                title={
                  student.teacherFlag.reason
                    ? `${t(`teacher.flags.reasonCategories.${student.teacherFlag.reason}`)}: ${student.teacherFlag.comment}`
                    : student.teacherFlag.comment
                }
              />
            </div>
          )}
        </td>
        <td className={`${topBorder} ${rowLabel}`}>{t('teacher.students.groupTable.rowPresence')}</td>
        {dotSessions.map((session) => (
          <td key={session.eventId} className={`${topBorder} ${dotCell}`}>
            <span className="inline-flex justify-center">
              <SessionDot session={session} kind="presence" studentId={student.studentId} size="sm" />
            </span>
          </td>
        ))}
        <td rowSpan={2} className={`${topBorder} ${totalCell}`}>
          {student.metrics.presence.rate !== null ? `${student.metrics.presence.rate}%` : t('teacher.flags.metrics.noData')}
        </td>
        <td rowSpan={2} className={`${topBorder} ${totalCell}`}>
          {student.metrics.homework.rate !== null ? `${student.metrics.homework.rate}%` : t('teacher.flags.metrics.noData')}
        </td>
        <td rowSpan={2} className={`${topBorder} ${totalCell}`}>
          <div className="flex flex-col items-center gap-1">
            <FlagPill level={student.autoFlag.level} title={autoTitle} />
            <button
              type="button"
              onClick={onFlag}
              className="text-[11px] text-[#cf2e2e] hover:underline"
            >
              {t('teacher.students.flagButton')}
            </button>
          </div>
        </td>
      </tr>
      <tr>
        <td className={rowLabel}>{t('teacher.students.groupTable.rowHomework')}</td>
        {dotSessions.map((session) => (
          <td key={session.eventId} className={dotCell}>
            <span className="inline-flex justify-center">
              <SessionDot session={session} kind="homework" studentId={student.studentId} size="sm" />
            </span>
          </td>
        ))}
      </tr>
    </>
  );
}

function EmptyHint({ text }: { text: string }) {
  return <p className="rounded-lg border border-dashed border-rose-200 px-4 py-3 text-sm text-gray-500">{text}</p>;
}
